import React, { useEffect, useMemo, useState } from 'react';
import { authToken } from '../../authToken';
import { API_BASE_URL } from '../../config';
import ModalShell from '../../components/ModalShell';
import FoodIntakeAddPanel from './FoodIntakeAddPanel';
import FoodIntakeItemMenuPanel from './FoodIntakeItemMenuPanel';
import FoodIntakeEditPanel from './FoodIntakeEditPanel';
import { formatEpochMillisLocal, getFoodIntakeTotals, sortByTimestampDesc } from './foodIntakeUtils';

const ENDPOINT_HOST = API_BASE_URL;
const ENDPOINT_FOOD = `${ENDPOINT_HOST}/foodintake`;
const ENDPOINT_FOOD_COMMIT = `${ENDPOINT_FOOD}/commit`;

const targetKcal = 2100;
const targetC = 250;
const limitP = 100;
const limitF = 80;

const percentOf = (value, target) => {
  if (!target || target <= 0) return 0;
  const v = Number(value) || 0;
  return Math.round((v / target) * 100);
};

const remainingOf = (value, target) => {
  const v = Number(value) || 0;
  const t = Number(target) || 0;
  return t - v;
};

export default function FoodIntakePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeSubPanel, setActiveSubPanel] = useState(null); // 'add'

  const [activeItem, setActiveItem] = useState(null);
  const [showMenuForId, setShowMenuForId] = useState(null);
  const [showEditForItem, setShowEditForItem] = useState(null);
  const [confirmDeleteForItem, setConfirmDeleteForItem] = useState(null);
  const [confirmCommit, setConfirmCommit] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(ENDPOINT_FOOD, {
        headers: {
          ...(authToken ? { 'Authorization': authToken } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch food intake (${response.status})`);
      }

      const data = await response.json();
      setItems(sortByTimestampDesc(data));
    } catch (e) {
      console.error('Failed to fetch food intake:', e);
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const totals = useMemo(() => getFoodIntakeTotals(items), [items]);

  const sortedItems = useMemo(() => sortByTimestampDesc(items), [items]);

  const handleCommit = async () => {
    setError(null);

    try {
      const response = await fetch(ENDPOINT_FOOD_COMMIT, {
        method: 'POST',
        headers: {
          ...(authToken ? { 'Authorization': authToken } : {})
        }
      });

      if (!response.ok) {
        const msg = await response.text();
        throw new Error(msg || `Commit failed (${response.status})`);
      }

      await fetchItems();
      setActiveSubPanel(null);
    } catch (e) {
      console.error('Food intake commit failed:', e);
      setError(e?.message || 'Commit failed');
    }
  };

  const openCommitConfirm = () => {
    setConfirmCommit(true);
  };

  const openMenu = (item) => {
    setActiveItem(item);
    setShowMenuForId(item?.id ?? '__noid__');
  };

  const closeMenu = () => {
    setShowMenuForId(null);
    setActiveItem(null);
  };

  const handleDelete = async (item) => {
    const id = item?.id;
    if (id === null || id === undefined) return;

    try {
      const response = await fetch(`${ENDPOINT_FOOD}/${id}`, {
        method: 'DELETE',
        headers: {
          ...(authToken ? { 'Authorization': authToken } : {})
        }
      });

      if (!response.ok) {
        alert(`Failed to delete (${response.status})`);
        return;
      }

      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      console.error('Failed to delete food intake:', e);
      alert('Failed to delete');
    }
  };

  const itemCardStyle = {
    padding: '10px 12px',
    margin: '6px 0',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 10,
    alignItems: 'center'
  };

  return (
    <div style={{ padding: '12px 0' }}>
      <h3 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>🍽️ Food Intake</h3>

      <div style={{
        display: 'flex',
        gap: 18,
        marginBottom: 14,
        fontSize: '0.98rem',
        fontWeight: 600,
        color: '#333',
        justifyContent: 'space-between',
        background: '#fff3e6',
        borderRadius: 8,
        padding: '10px 14px',
        border: '1px solid #ff8a00',
      }}>
        <span>kcal: <b>{totals.kcal}</b></span>
        <span>C: <b>{totals.carbs}</b></span>
        <span>S: <b>{totals.sugarInCarbs}</b></span>
        <span>P: <b>{totals.protein}</b></span>
        <span>F: <b>{totals.fat}</b></span>
      </div>

      <div style={{
        display: 'flex',
        gap: 18,
        marginBottom: 14,
        fontSize: '0.92rem',
        fontWeight: 600,
        color: '#333',
        justifyContent: 'space-between',
        background: '#fff3e6',
        borderRadius: 8,
        padding: '10px 14px',
        border: '1px solid #ff8a00',
      }}>
        <span>kcal: <b>{percentOf(totals.kcal, targetKcal)}%</b></span>
        <span>C: <b>{percentOf(totals.carbs, targetC)}%</b></span>
        <span>P: <b>{percentOf(totals.protein, limitP)}%</b></span>
        <span>F: <b>{percentOf(totals.fat, limitF)}%</b></span>
      </div>

      <div style={{
        display: 'flex',
        gap: 18,
        marginBottom: 14,
        fontSize: '0.92rem',
        fontWeight: 600,
        color: '#333',
        justifyContent: 'space-between',
        background: '#fff3e6',
        borderRadius: 8,
        padding: '10px 14px',
        border: '1px solid #ff8a00',
      }}>
        <span>kcal: <b>{remainingOf(totals.kcal, targetKcal)}</b></span>
        <span>C: <b>{remainingOf(totals.carbs, targetC)}</b></span>
        <span>P: <b>{remainingOf(totals.protein, limitP)}</b></span>
        <span>F: <b>{remainingOf(totals.fat, limitF)}</b></span>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <button
          style={{
            flex: 1,
            padding: '12px 16px',
            backgroundColor: '#ff8a00',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={() => setActiveSubPanel('add')}
        >
          ➕ Add
        </button>
        <button
          style={{
            flex: 1,
            padding: '12px 16px',
            backgroundColor: '#ff8a00',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={openCommitConfirm}
        >
          ✓ Commit
        </button>
      </div>

      {loading && <div style={{ textAlign: 'center', color: '#666' }}>Loading...</div>}
      {error && <div style={{ textAlign: 'center', color: 'red' }}>Error: {error}</div>}

      {!loading && !error && sortedItems.length === 0 && activeSubPanel !== 'add' && (
        <div style={{ textAlign: 'center', color: '#666', padding: '10px 0' }}>
          No food items yet. Use “Add”.
        </div>
      )}

      {activeSubPanel === 'add' && (
        <ModalShell
          height={650}
        >
          <FoodIntakeAddPanel
            onClose={() => {
              setActiveSubPanel(null);
              fetchItems();
            }}
          />
        </ModalShell>
      )}

      {!loading && !error && sortedItems.length > 0 && (
        <div style={{ maxHeight: '360px', overflowY: 'auto', paddingTop: 6 }}>
          {sortedItems.map((it) => (
            <div key={it.id ?? `${it.timestamp}-${it.name}`} style={itemCardStyle}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.name || '(no name)'}
                  </div>
                  <div style={{ color: '#666', fontSize: 13, flexShrink: 0 }}>
                    {formatEpochMillisLocal(it.timestamp)}
                  </div>
                </div>

                <div style={{ marginTop: 6, color: '#333', fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <span><b>C</b>: {it.carbs}</span>
                  <span><b>S</b>: {it.sugarInCarbs ?? 0}</span>
                  <span><b>P</b>: {it.protein}</span>
                  <span><b>F</b>: {it.fat}</span>
                  <span><b>kcal</b>: {it.kcal}</span>
                </div>
              </div>

              <button
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  cursor: 'pointer',
                  color: '#888',
                  padding: '4px 6px'
                }}
                aria-label="Menu"
                onClick={() => openMenu(it)}
              >
                ⋯
              </button>
            </div>
          ))}
        </div>
      )}

      {showMenuForId && activeItem && (
        <FoodIntakeItemMenuPanel
          onEdit={() => {
            setShowEditForItem(activeItem);
            closeMenu();
          }}
          onDelete={() => {
            setConfirmDeleteForItem(activeItem);
            closeMenu();
          }}
          onCancel={closeMenu}
        />
      )}

      {showEditForItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            maxWidth: '520px',
            width: '92%',
            maxHeight: '92vh',
            overflow: 'auto'
          }}>
            <FoodIntakeEditPanel
              item={showEditForItem}
              onClose={(changed) => {
                setShowEditForItem(null);
                if (changed) fetchItems();
              }}
            />
          </div>
        </div>
      )}

      {confirmDeleteForItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            maxWidth: '320px',
            width: '90%'
          }}>
            <h4 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>Delete Item</h4>
            <p style={{ margin: '0 0 20px 0', textAlign: 'center', fontSize: '14px' }}>
              Delete <strong>{confirmDeleteForItem.name}</strong> from{' '}
              <strong>{formatEpochMillisLocal(confirmDeleteForItem.timestamp)}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setConfirmDeleteForItem(null)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const it = confirmDeleteForItem;
                  setConfirmDeleteForItem(null);
                  await handleDelete(it);
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmCommit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            maxWidth: '320px',
            width: '90%'
          }}>
            <h4 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>Commit</h4>
            <p style={{ margin: '0 0 20px 0', textAlign: 'center', fontSize: '14px' }}>
              Commit all current food intake entries?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setConfirmCommit(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setConfirmCommit(false);
                  await handleCommit();
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#ff8a00',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Commit
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
